import z from "../zod";
import { IdSchema } from "./common.schema";

export const CUSTOM_QUESTION_TEXT_MAX_LENGTH = 5_000;
export const CUSTOM_QUESTION_SIGNATURE_MAX_LENGTH = 64_000;
export const CUSTOM_QUESTION_SIGNATURE_MAX_BYTES = 48_000;

const PNG_DATA_URL_PREFIX = "data:image/png;base64,";
const BASE64_PATTERN =
  /^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/;
const PNG_MAGIC_BASE64_PREFIX = "iVBORw0KGgo";

export const isPngDataUrl = (value: string): boolean => {
  if (
    value.length > CUSTOM_QUESTION_SIGNATURE_MAX_LENGTH ||
    !value.startsWith(PNG_DATA_URL_PREFIX)
  ) {
    return false;
  }

  const payload = value.slice(PNG_DATA_URL_PREFIX.length);
  if (
    !payload.startsWith(PNG_MAGIC_BASE64_PREFIX) ||
    !BASE64_PATTERN.test(payload)
  ) {
    return false;
  }

  const padding = payload.endsWith("==") ? 2 : payload.endsWith("=") ? 1 : 0;
  const decodedBytes = (payload.length / 4) * 3 - padding;
  return decodedBytes <= CUSTOM_QUESTION_SIGNATURE_MAX_BYTES;
};

export const PngDataUrlSchema = z
  .string()
  .max(CUSTOM_QUESTION_SIGNATURE_MAX_LENGTH)
  .refine(isPngDataUrl, "Must be a valid PNG data URL")
  .meta({
    title: "PNG Signature",
    description: "A bounded PNG image encoded as a base64 data URL.",
  });

export const CustomQuestionTypeEnum = z.enum([
  "checkbox",
  "date",
  "daterange",
  "dropdown",
  "number",
  "signature",
  "text",
  "textarea",
]);

export const CustomQuestionAudienceEnum = z.enum(["candidate", "staff"]);
export const CustomQuestionFixedRoleEnum = z.enum(["fullName", "signature"]);

export const CustomQuestionOptionSchema = z
  .object({
    value: z.string().min(1).max(200).meta({
      title: "Value",
      description: "The value stored when this option is selected.",
    }),
    label: z.string().min(1).max(200).meta({
      title: "Label",
      description: "The option displayed to the person answering the question.",
    }),
  })
  .meta({
    title: "Custom Question Option",
    description: "An available answer for a custom dropdown question.",
  });

export const CustomQuestionConditionRuleSchema = z
  .object({
    questionKey: z.string().min(1).max(128).meta({
      title: "Question Key",
      description: "The stable key of the question that controls visibility.",
    }),
    operator: z.enum(["equals", "notEquals"]).meta({
      title: "Operator",
      description: "How the controlling answer is compared with the value.",
    }),
    value: z
      .union([
        z.string().max(CUSTOM_QUESTION_TEXT_MAX_LENGTH),
        z.boolean(),
        z.number(),
        z.null(),
      ])
      .meta({
        title: "Value",
        description: "The answer value used to determine visibility.",
      }),
  })
  .meta({
    title: "Custom Question Condition Rule",
    description: "A comparison used to control custom-question visibility.",
  });

export const CustomQuestionConditionSchema = z
  .object({
    mode: z.enum(["any", "all"]).meta({
      title: "Mode",
      description:
        "Whether any rule or every rule must match before the question is shown.",
    }),
    rules: z.array(CustomQuestionConditionRuleSchema).min(1).max(20).meta({
      title: "Rules",
      description: "The comparisons used to determine question visibility.",
    }),
  })
  .meta({
    title: "Custom Question Condition",
    description: "Rules that control when a custom question is shown.",
  });

export const CustomQuestionSchema = z
  .object({
    id: IdSchema.optional().meta({
      title: "Question ID",
      description:
        "The unique question identifier. Omit it when adding a question; an identifier will be assigned automatically.",
    }),
    key: z.string().min(1).max(128).meta({
      title: "Key",
      description:
        "A stable key used to associate the question with current and historical answers.",
    }),
    label: z.string().min(1).max(500).meta({
      title: "Label",
      description: "The question shown to the person completing the form.",
    }),
    description: z.string().max(2000).optional().meta({
      title: "Description",
      description: "Optional supporting text displayed with the question.",
    }),
    type: CustomQuestionTypeEnum.meta({
      title: "Type",
      description: "The type of answer collected for the question.",
    }),
    required: z.boolean().meta({
      title: "Required",
      description:
        "Whether an answer is required while the question is visible.",
    }),
    enabled: z.boolean().meta({
      title: "Enabled",
      description:
        "Whether the question is included in new forms. Historical responses retain disabled questions.",
    }),
    order: z.number().int().min(0).max(1000).meta({
      title: "Order",
      description: "The display order of the question.",
    }),
    condition: CustomQuestionConditionSchema.optional().meta({
      title: "Condition",
      description: "Optional rules that determine when the question is shown.",
    }),
    options: z
      .array(CustomQuestionOptionSchema)
      .min(1)
      .max(100)
      .optional()
      .meta({
        title: "Options",
        description: "The available answers for a dropdown question.",
      }),
    default: z.boolean().optional().meta({
      title: "Default",
      description: "Whether this question belongs to the standard template.",
    }),
    audience: CustomQuestionAudienceEnum.optional().meta({
      title: "Answering Audience",
      description:
        "Who is expected to answer the question. When omitted, the question may be answered through both candidate and staff workflows.",
    }),
    fixedRole: CustomQuestionFixedRoleEnum.optional().meta({
      title: "Candidate Attestation Role",
      description:
        "The candidate attestation value represented by this protected question. Staff submissions cannot replace these answers.",
    }),
  })
  .meta({
    title: "Custom Question",
    description: "A configurable question in a reusable question set.",
  });

export const CustomQuestionInputSchema = CustomQuestionSchema.omit({
  audience: true,
  fixedRole: true,
})
  .extend({
    key: CustomQuestionSchema.shape.key.optional().meta({
      title: "Key",
      description:
        "The stable question key. Omit it for a new question and one will be assigned automatically.",
    }),
  })
  .meta({
    title: "Custom Question Input",
    description: "A custom question supplied when updating a question set.",
  });

export const CustomQuestionSetSchema = z
  .object({
    questions: z.array(CustomQuestionSchema).max(100).meta({
      title: "Questions",
      description: "The ordered questions included in the question set.",
    }),
  })
  .meta({
    title: "Custom Question Set",
    description: "An ordered, point-in-time set of configurable questions.",
  });

export const CustomQuestionConfigurationSchema = CustomQuestionSetSchema.extend(
  {
    updatedAt: z.iso.datetime().optional().meta({
      title: "Questions Updated At",
      description: "When this question configuration was last updated.",
    }),
  },
).meta({
  title: "Custom Question Configuration",
  description: "An ordered custom question configuration used for new forms.",
});

export const CustomQuestionSetInputSchema = CustomQuestionSetSchema.extend({
  questions: z.array(CustomQuestionInputSchema).max(100).meta({
    title: "Questions",
    description: "The ordered questions to save in the set.",
  }),
}).meta({
  title: "Custom Question Set Input",
  description: "A custom question set supplied for configuration.",
});

export const CustomQuestionAnswerValueSchema = z.union([
  z.string().max(CUSTOM_QUESTION_TEXT_MAX_LENGTH),
  PngDataUrlSchema,
  z.boolean(),
  z.number(),
  z.null(),
]);

export const CustomQuestionAnswerSchema = z
  .object({
    questionId: IdSchema.meta({
      title: "Question ID",
      description: "The identifier of the question being answered.",
    }),
    questionKey: z.string().min(1).max(128).meta({
      title: "Question Key",
      description: "The stable key of the question being answered.",
    }),
    value: CustomQuestionAnswerValueSchema.meta({
      title: "Value",
      description: "The submitted answer to the question.",
    }),
  })
  .meta({
    title: "Custom Question Answer",
    description: "An answer to a custom question.",
  });

export const CustomQuestionAnswerInputSchema =
  CustomQuestionAnswerSchema.extend({
    questionKey: CustomQuestionAnswerSchema.shape.questionKey.optional().meta({
      title: "Question Key",
      description:
        "The stable question key. Older consumers may omit it when a question ID is supplied.",
    }),
  }).meta({
    title: "Custom Question Answer Input",
    description: "An answer supplied for a custom question.",
  });

export const CustomQuestionResponseSetSchema = CustomQuestionSetSchema.extend({
  answers: z.array(CustomQuestionAnswerSchema).max(100).meta({
    title: "Answers",
    description: "The answers recorded for the point-in-time question set.",
  }),
  submittedAt: z.iso.datetime().optional().meta({
    title: "Submitted At",
    description: "When the question set was submitted.",
  }),
  submittedIp: z.string().max(64).optional().meta({
    title: "Submission IP Address",
    description:
      "The network address recorded when the response was submitted.",
  }),
  configurationUpdatedAt: z.iso.datetime().optional().meta({
    title: "Answered Question Set Updated At",
    description:
      "When the question configuration captured by this response was last updated.",
  }),
}).meta({
  title: "Custom Question Response Set",
  description:
    "An immutable record of a question set and its answers at the time it was submitted.",
});

export const CustomQuestionResponseLifecycleSchema = z
  .object({
    currentConfigurationUpdatedAt: z.iso.datetime().optional().meta({
      title: "Current Question Set Updated At",
      description:
        "When the question configuration currently used for new forms was last updated.",
    }),
    questionsHaveChanged: z.boolean().meta({
      title: "Questions Have Changed",
      description:
        "Whether the questions currently used for new forms differ from those captured by this response.",
    }),
    isLegacyFallback: z.boolean().meta({
      title: "Legacy Response",
      description:
        "Whether this response was reconstructed from an existing record that predates stored question snapshots.",
    }),
  })
  .meta({
    title: "Custom Question Response Lifecycle",
    description:
      "Information describing how a stored response relates to the current question configuration.",
  });

export const CustomQuestionWithAnswerSchema = CustomQuestionSchema.extend({
  value: CustomQuestionAnswerValueSchema.optional().meta({
    title: "Value",
    description: "The submitted answer, when one has been recorded.",
  }),
}).meta({
  title: "Custom Question With Answer",
  description: "A custom question combined with its submitted answer.",
});
